import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

function PasswordVerification({ onSuccess, onClose, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockActive, setCapsLockActive] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleKeyActivity = (e) => {
    if (e.getModifierState) {
      setCapsLockActive(e.getModifierState("CapsLock"));
    }
  };

  const handleVerify = async (e) => {
    e?.preventDefault();
    if (!password.trim() || loading) return;

    try {
      setLoading(true);
      setError("");
      const response = await axios.post(
        `${apiBaseUrl}/common/verify-password`,
        { password },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        if (response.data.token) {
          sessionStorage.setItem("authToken", response.data.token);
        }
        onSuccess?.();
      } else {
        setError("Invalid master password. Please try again.");
        inputRef.current?.select();
      }
    } catch (err) {
      console.error("Password verify error:", err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Network error — could not verify with server.");
      }
      inputRef.current?.select();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 select-none"
      style={zIndex ? { zIndex } : { zIndex: 9999999 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="w-full max-w-[345px] bg-white border border-slate-200 shadow-2xl shadow-slate-900/40 overflow-hidden transform transition-all duration-300 rounded-xl animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Inside Modal Body */}
        <form
          className="p-4 space-y-3 bg-white text-slate-800"
          onSubmit={handleVerify}
        >
          {/* Security Avatar & Context Info */}
          <div className="flex items-center space-x-3 pb-2.5 border-b border-slate-100">
            <div className="relative shrink-0">
              <div className="w-9 h-9 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shadow-xs">
                <svg
                  className="w-4.5 h-4.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border border-white flex items-center justify-center">
                <svg
                  className="w-1.5 h-1.5 text-white"
                  fill="currentColor"
                  viewBox="0 0 12 12"
                >
                  <path d="M3.707 5.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L5 7.586 3.707 5.293z" />
                </svg>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-slate-900 leading-tight">
                Enter Password
              </h3>
              <p className="text-[11px] text-slate-500 leading-tight mt-0.5">
                Enter master password to continue
              </p>
            </div>
          </div>

          {/* Password Input Form Control */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-[11px]">
              <label
                className="font-medium text-slate-700 flex items-center space-x-1"
                htmlFor="master-password"
              >
                <span>Master Password</span>
                <span className="text-rose-500">*</span>
              </label>

              {/* Caps Lock warning indicator badge */}
              {capsLockActive && (
                <span className="text-[9px] text-amber-700 font-medium px-1 py-0.2 bg-amber-50 border border-amber-300 rounded animate-pulse">
                  CAPS LOCK ON
                </span>
              )}
            </div>

            {/* Input Container with Prefix Icon and Visibility Toggle */}
            <div className="relative rounded-md shadow-xs">
              <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400">
                <svg
                  className="w-3.5 h-3.5 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <input
                ref={inputRef}
                id="master-password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyActivity}
                onKeyUp={handleKeyActivity}
                placeholder="••••••••••••"
                className="block w-full box-border pl-8 pr-8 py-1.5 text-xs font-medium text-slate-800 bg-white border border-slate-300 rounded-md placeholder-slate-400 tracking-wider focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition"
                required
                autoComplete="current-password"
              />

              {/* Show/Hide Password Eye Toggle */}
              <button
                type="button"
                aria-label="Toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-slate-400 hover:text-slate-600 transition focus:outline-none cursor-pointer border-none bg-transparent"
              >
                {showPassword ? (
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                    <path
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                )}
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-1.5 text-[11px] text-rose-600 bg-rose-50 border border-rose-200 px-2 py-1 rounded mt-1 animate-fadeIn">
                <svg
                  className="w-3 h-3 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Quick helper tip */}
            <div className="flex items-center justify-between text-[10px] text-slate-500 pt-0.5">
              <span>
                Press{" "}
                <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-200 rounded text-[9px] text-slate-600 font-mono">
                  Enter ↵
                </kbd>
              </span>
            </div>
          </div>

          {/* Dialog Action Buttons */}
          <div className="pt-1 flex items-center justify-end space-x-2">
            {/* Cancel Button */}
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-md transition flex items-center space-x-1 focus:outline-none focus:ring-1 focus:ring-slate-300 cursor-pointer"
            >
              <span>Cancel</span>
              <span className="text-[9px] opacity-60 font-mono">[Esc]</span>
            </button>

            {/* Verify & Authenticate CTA Button */}
            <button
              type="submit"
              disabled={loading}
              className="px-3.5 py-1.5 text-xs font-bold text-slate-950 bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 rounded-md shadow-xs shadow-amber-500/20 border border-amber-300 transition flex items-center space-x-1.5 focus:outline-none focus:ring-1 focus:ring-amber-300 active:scale-95 cursor-pointer disabled:opacity-60"
            >
              {loading ? (
                <>
                  <svg
                    className="w-3 h-3 animate-spin text-slate-900"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                  <span>Verifying...</span>
                </>
              ) : (
                <>
                  <svg
                    className="w-3.5 h-3.5 text-slate-900"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.2"
                    />
                  </svg>
                  <span>Verify</span>
                  <span className="text-[9px] opacity-75 font-mono">
                    [↵]
                  </span>
                </>
              )}
            </button>
          </div>
        </form>

        {/* Security Compliance Footer Bar */}
        <div className="h-7 bg-slate-50 border-t border-slate-100 px-3 flex items-center justify-between text-[10px] text-slate-500">
          <div className="flex items-center space-x-1 text-slate-500">
            <svg
              className="w-3 h-3 text-emerald-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                clipRule="evenodd"
                d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                fillRule="evenodd"
              />
            </svg>
            <span>Encrypted Session</span>
          </div>
          <div className="text-slate-500 font-mono text-[9px]">
            Protected System
          </div>
        </div>
      </div>
    </div>
  );
}

export default PasswordVerification;
